<template>
  <v-card flat class="bg-background pa-5">
    <template v-if="savedTripsGroupedByEV.length">
      <v-list
        v-model:selected="selection"
        three-line
        class="bg-pwt_alabaster"
        active-class="text-pwt_black"
        selectable
        select-strategy="leaf"
        multiple
      >
        <template v-for="group in savedTripsGroupedByEV">
          <v-list-subheader class="text-pwt_black bg-pwt_alabaster">
            {{ getModelName(group.evModel) || 'Unknown' }}
          </v-list-subheader>
          <v-list-subheader>
            <v-row no-gutters>
              {{ group.trips.length }} saved route{{ group.trips.length > 1 ? 's' : '' }}
              {{ getNumberOfTripsTravelledInGroup(group).toLocaleString() }}
              frequent trip{{ getNumberOfTripsTravelledInGroup(group) === 1 ? '' : 's' }}
              per year
            </v-row>
          </v-list-subheader>
          <v-list-item
            v-for="trip in group.trips"
            :key="trip.localId"
            :value="trip.localId"
            :title="trip.displayName"
            :subtitle="getNumberOfTripsTravelledInTrip(trip).toLocaleString() + ' trips per year'"
            class="text-pwt_black bg-pwt_alabaster"
          >
            <template v-slot:prepend="{ isSelected }">
              <v-list-item-action start>
                <v-icon v-if="isSelected" color="success"> mdi-check-circle </v-icon>
                <v-icon v-else color="primary"> mdi-map-outline </v-icon>
              </v-list-item-action>
            </template>
          </v-list-item>
        </template>
      </v-list>
      <v-card-actions>
        <v-spacer></v-spacer>
        <ElevatedBtn @click="applyFilters"> Apply filter </ElevatedBtn>
        <OutlinedBtn @click="clearFilters"> Clear filter </OutlinedBtn>
      </v-card-actions>
    </template>
    <template v-else>
      <v-card-title> No saved trips </v-card-title>
      <v-card-text> You have no saved trips with a frequency to use in savings calculations </v-card-text>
    </template>
  </v-card>
</template>
<script lang="ts" setup>
import Trip from '@/classes/trip_classes/trip'
import { GettersTypes, MutationTypes, type State } from '@/store/store_types'
import { type SortedTripsGroup } from '@/types/sheared_local_types'
import { calcFrequencyAdjustment } from '@/utils/calcYearsSavings'
import sortTripsViaEVGroups from '@/utils/sortTripsViaEVGroups'
import { computed, nextTick, onMounted, ref, watch } from 'vue'
import { useStore } from 'vuex'
import ElevatedBtn from '../../ui-elements/buttons/ElevatedBtn.vue'
import OutlinedBtn from '../../ui-elements/buttons/OutlinedBtn.vue'

const emit = defineEmits(['close'])
const store = useStore<State>()
const selection = ref<string[]>([])
const evModels = computed(() => store.state.evModels)
const savedTrips = computed<Trip[]>(() => store.getters[GettersTypes.savedTrips])
const savedTripsGroupedByEV = computed(() => sortTripsViaEVGroups(savedTrips.value))
const filteredSavedTrips = computed(() => store.state.filteredSavingsTrips)

/**
 * Returns the name of the EV model given its ID, or undefined if no match
 * is found.
 * @param id The ID of the EV model to search for.
 * @returns The name of the EV model or undefined.
 */
function getModelName(id: string): string | undefined {
  return evModels.value.find((model) => model.id === id)?.name
}

/**
 * Calculates the total number of trips per year, taking into account
 * the frequency of each trip.
 * @param group The group to calculate the total number of trips for.
 * @returns The total number of trips in the given group, rounded up to the
 * nearest integer.
 */
function getNumberOfTripsTravelledInGroup(group: SortedTripsGroup): number {
  let count = 0
  group.trips.forEach((trip) => {
    if (trip.frequency) {
      count += calcFrequencyAdjustment(trip.frequency)
    }
  })
  return Math.round(count)
}

/**
 * Calculates the total number of trips a given trip is equivalent to, taking into
 * account the frequency of the trip.
 * @param trip The trip to calculate the total number of trips for.
 * @returns The total number of trips the given trip is equivalent to, rounded up to
 * the nearest integer.
 */
function getNumberOfTripsTravelledInTrip(trip: Trip): number {
  return trip.frequency ? Math.round(calcFrequencyAdjustment(trip.frequency)) : 1
}

/**
 * Clears the current filter selection and resets the group selection to
 * all groups.
 */
function clearFilters() {
  selection.value = []
  store.commit(MutationTypes.setFilteredSavingsTrips, undefined)
}

/**
 * Applies the current filter selection to the trip groups. If a group's EV
 * model ID matches one of the IDs in the selection, it is added to the
 * temporary array. The temporary array is then passed to the Vuex store
 * mutation to update the trip groups. Finally, the group selection dialog
 * is closed.
 */
function applyFilters() {
  store.commit(MutationTypes.setFilteredSavingsTrips, selection)
  emit('close')
}

function setInitialValue() {
  selection.value = filteredSavedTrips.value ?? []
}

onMounted(() => {
  nextTick(() => {
    setInitialValue()
  })
})

watch(
  filteredSavedTrips,
  () => {
    setInitialValue()
  },
)

</script>
